import { useEffect, useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { Search } from 'lucide-react'
import { Logo } from '@/components/ui/logo'
import { Button } from '@/components/ui/button'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { AccountSwitcher } from './AccountSwitcher'
import { DashboardPageProps } from '@/types'

type TopBarDestination = {
  label: string
  href: string
}

export function TopBar() {
  const { auth, sidebar } = usePage<DashboardPageProps>().props
  const accountSlug = auth.account?.slug ?? ''
  const [open, setOpen] = useState(false)

  const commandDestinations: TopBarDestination[] = [
    { label: 'Businesses', href: `/${accountSlug}/businesses` },
  ]

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setOpen((value) => !value)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const visit = (href: string) => {
    setOpen(false)
    router.visit(href)
  }

  return (
    <header className="sticky top-0 z-20 flex h-14 w-full shrink-0 items-center gap-2 border-b bg-background px-3 sm:px-4">
      <Link href={`/${accountSlug}/businesses`} className="flex shrink-0 items-center">
        <Logo size="sm" />
      </Link>

      <div className="flex min-w-0 flex-1 items-center justify-center px-1 sm:px-4">
        <div className="w-10 shrink-0 sm:w-full sm:max-w-lg sm:shrink">
          <Button
            variant="outline"
            aria-label="Search"
            className="size-10 rounded-xl p-0 shadow-none sm:h-9 sm:w-full sm:justify-start sm:gap-2.5 sm:px-3"
            onClick={() => setOpen(true)}
          >
            <Search className="size-4 shrink-0 text-muted-foreground" />
            <span className="hidden text-sm text-muted-foreground sm:inline">Search…</span>
            <kbd className="pointer-events-none ml-auto hidden h-5 items-center rounded-md border border-border/70 bg-background px-1.5 font-mono text-xs font-medium text-muted-foreground md:inline-flex">
              ⌘K
            </kbd>
          </Button>
        </div>
      </div>

      <AccountSwitcher />

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Type a command or search…" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Navigate">
            {commandDestinations.map((item) => (
              <CommandItem key={item.href} onSelect={() => visit(item.href)}>
                {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
          {(sidebar?.permissions?.can_view_settings || auth.user) && (
            <CommandGroup heading="Account">
              {sidebar?.permissions?.can_view_settings && (
                <CommandItem onSelect={() => visit(`/${accountSlug}/settings`)}>
                  Settings
                </CommandItem>
              )}
              {auth.user && (
                <CommandItem onSelect={() => visit(`/${accountSlug}/profile/edit`)}>
                  Profile
                </CommandItem>
              )}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </header>
  )
}
